import { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';

type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'complykit-theme';

// Applies the theme class before the browser paints; falls back to
// useEffect on the server, where layout effects would warn.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const resolveInitialTheme = (): Theme => {
  if (typeof window === 'undefined') {
    return 'light';
  }
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

const ThemeContext = createContext({
  theme: 'light',
  setTheme: (_theme: Theme) => {
    // Replaced by the actual implementation in ThemeProvider
  },
  toggleTheme: () => {
    // Replaced by the actual implementation in ThemeProvider
  },
});

export const useTheme = () => useContext(ThemeContext);

/**
 * Applies the ComplyKit theme by toggling the `dark` class on the document
 * root, which drives every token CSS variable.
 *
 * Standalone (no `forcedTheme`): the initial theme is resolved before the
 * first render (persisted choice, then OS preference) and the class is
 * applied before paint, so the provider itself never causes a theme flash.
 * Changes made through setTheme/toggleTheme are persisted.
 *
 * Follower (`forcedTheme` set): the host appearance system is the single
 * source of truth (Chakra's color mode in the product apps) and this
 * provider only mirrors it; setTheme and toggleTheme are inert. The class
 * still only applies when React runs, so hosts must also set the initial
 * class in a blocking inline script before the bundle loads (see the app
 * `index.html` files and the theming section of
 * docs/specs/frontend/design-system/design-system.md).
 */
export const ThemeProvider = ({
  children,
  forcedTheme,
}: {
  children: React.ReactNode;
  forcedTheme?: Theme;
}) => {
  const [preference, setPreference] = useState<Theme>(resolveInitialTheme);
  const theme = forcedTheme ?? preference;

  useIsomorphicLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Reset only when the provider unmounts, never between theme changes.
  useEffect(() => {
    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, []);

  // Dev-only wiring guard: a host that renders ComplyKit without loading the
  // theme CSS fails silently (unstyled controls), so make it loud instead.
  // import.meta.env is statically replaced by Vite in dev and build, so the
  // block is dead-code eliminated from production bundles. MODE (not DEV)
  // keeps the guard quiet under vitest, whose MODE is "test".
  useEffect(() => {
    if (import.meta.env.MODE !== 'development') return;
    const probe = getComputedStyle(document.documentElement).getPropertyValue('--ck-accent-9');
    if (!probe) {
      // eslint-disable-next-line no-console -- dev-only wiring guard
      console.error(
        'ComplyKit: the theme CSS is not loaded. Import @main/complykit/theme/complykit.built.css once in the app entry (see docs/specs/frontend/design-system/design-system.md).',
      );
    }
  }, []);

  const setTheme = (next: Theme) => {
    if (forcedTheme) {
      if (process.env['NODE_ENV'] !== 'production') {
        // eslint-disable-next-line no-console -- dev-only misuse warning, stripped in production
        console.warn(
          'ComplyKit ThemeProvider: setTheme is ignored while forcedTheme is set; change the host theme instead.',
        );
      }
      return;
    }
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Storage unavailable (private mode); the in-memory theme still applies.
    }
    setPreference(next);
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
